import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { importApi } from '../api/endpoints.js';
import { useToast } from '../hooks/useToast.jsx';
import { useApiError } from '../hooks/useApiError.js';
import Modal from './Modal.jsx';

/**
 * Load a list of companies into the CRM as prospects "not called yet".
 *
 * Two steps, on purpose: the file is first sent as a dry run, so the numbers
 * and the rejected lines on screen come from the same parser that will do the
 * real write - the preview cannot disagree with the result.
 *
 * The sample file is downloaded from the API rather than rebuilt here: one
 * definition of the expected columns, on the server, next to the parser.
 */
const ImportProspectsModal = ({ open, onClose, onImported }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const toMessage = useApiError();
  const fileRef = useRef(null);

  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCsv('');
    setFileName('');
    setReport(null);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  /** Ask the server what this file would do, without writing anything. */
  const analyse = async (text, name) => {
    setBusy(true);
    try {
      const result = await importApi.prospects(text, true);
      setCsv(text);
      setFileName(name);
      setReport(result);
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => analyse(String(reader.result), file.name);
    reader.onerror = () => toast.error(t('import.readError'));
    reader.readAsText(file, 'utf-8');
  };

  /** "Try it now": the sample file, loaded straight into the preview. */
  const loadDemo = async () => {
    try {
      await analyse(await importApi.sample(), t('import.demoFile'));
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const download = async () => {
    try {
      const text = await importApi.sample();
      const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'prospects-exemple.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const result = await importApi.prospects(csv, false);
      toast.success(t('import.done', { count: result.imported }));
      reset();
      // Straight back to the prospect table, now holding the new rows.
      onImported(result);
    } catch (err) {
      toast.error(toMessage(err));
      setBusy(false);
    }
  };

  const blocking = (report?.errors ?? []).filter((error) => !error.kept);
  const ready = Boolean(report && report.count > 0);

  return (
    <Modal
      open={open}
      title={t('import.title')}
      onClose={close}
      width={720}
      footer={
        <>
          <button type="button" className="btn" onClick={report ? reset : close}>
            {report ? t('common.back') : t('common.cancel')}
          </button>
          <button type="button" className="btn btn--primary" onClick={confirm} disabled={!ready || busy}>
            {busy ? t('common.saving') : t('import.confirm', { count: report?.count ?? 0 })}
          </button>
        </>
      }
    >
      {!report ? (
        <div className="import">
          <p className="import__hint">{t('import.hint')}</p>

          <p className="import__columns">
            <strong>{t('import.columns')}</strong> companyName, firstName, lastName, role, email, phone, website,
            industry, city, country, notes
          </p>
          <p className="import__hint">{t('import.columnsHint')}</p>

          <label className="import__drop">
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} disabled={busy} />
            <span>{busy ? t('common.loading') : t('import.chooseFile')}</span>
          </label>

          <div className="import__actions">
            <button type="button" className="btn btn--small" onClick={download}>
              {t('import.downloadSample')}
            </button>
            <button type="button" className="btn btn--small" onClick={loadDemo} disabled={busy}>
              {t('import.tryDemo')}
            </button>
          </div>
        </div>
      ) : (
        <div className="import">
          <p className="import__file">{fileName}</p>

          <div className="import__counts">
            <span className="import__count import__count--ok">
              <strong>{report.count}</strong> {t('import.willImport')}
            </span>
            {blocking.length ? (
              <span className="import__count import__count--ko">
                <strong>{blocking.length}</strong> {t('import.rejected')}
              </span>
            ) : null}
          </div>

          {report.preview?.length ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('customers.companyName')}</th>
                    <th>{t('customers.contact')}</th>
                    <th>{t('customers.email')}</th>
                    <th>{t('customers.phone')}</th>
                    <th>{t('customers.city')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.preview.map((row, index) => (
                    <tr key={`${row.companyName}-${index}`}>
                      <td>{row.companyName}</td>
                      <td>{[row.contactFirstName, row.contactLastName].filter(Boolean).join(' ') || '-'}</td>
                      <td>{row.email || '-'}</td>
                      <td>{row.phone || '-'}</td>
                      <td>{row.city || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {report.count > report.preview?.length ? (
            <p className="import__hint">{t('import.andMore', { count: report.count - report.preview.length })}</p>
          ) : null}

          {report.errors?.length ? (
            <ul className="import__errors">
              {report.errors.slice(0, 12).map((error) => (
                <li key={`${error.line}-${error.reason}`} className={error.kept ? 'import__error--kept' : ''}>
                  {t('import.line', { line: error.line })} — {t(`import.reasons.${error.reason}`)}
                  {error.value ? ` (${error.value})` : ''}
                </li>
              ))}
              {report.errors.length > 12 ? <li>…</li> : null}
            </ul>
          ) : null}
        </div>
      )}
    </Modal>
  );
};

export default ImportProspectsModal;
